import { useState, useCallback, useRef, useEffect } from 'react';
import * as api from '../api/storageApi';
import type { DriveFile, DriveFolder, SortKey, SortDir } from '../types';

const CURRENT_PATH_KEY = 'drive:current-path';

// ─── SWR cache compartido para listings ─────────────────────────────────
// Permite render instantáneo al volver a una carpeta visitada.
// Las mutaciones invalidan los prefijos afectados.
type CachedListing = {
  folders: DriveFolder[];
  files: DriveFile[];
  ts: number;
};
const listCache = new Map<string, CachedListing>();
const CACHE_TTL = 60_000; // 60s: tras este tiempo se revalida igualmente

const cacheKey = (p: string) => p.replace(/\/+$/, '');

export function invalidateListCache(...prefixes: string[]) {
  if (prefixes.length === 0) {
    listCache.clear();
    return;
  }
  for (const p of prefixes) listCache.delete(cacheKey(p));
}

function setCache(p: string, folders: DriveFolder[], files: DriveFile[]) {
  listCache.set(cacheKey(p), { folders, files, ts: Date.now() });
}

function getCache(p: string): CachedListing | undefined {
  return listCache.get(cacheKey(p));
}

/**
 * Helper compartido para componentes (MoveModal, etc.) que necesitan listar
 * carpetas sin volver a pegarle al API si ya están en caché.
 *
 * - Si hay caché fresca (< TTL) → la devuelve inmediatamente, sin fetch.
 * - Si no hay → hace fetch, decodifica nombres y guarda en caché.
 *
 * Comparte la misma `listCache` que `useDrive.load`, así que las navegaciones
 * previas del usuario aceleran las cargas del modal.
 */
export async function listFoldersCached(prefix: string): Promise<DriveFolder[]> {
  const cached = getCache(prefix);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.folders;
  }

  const result = await api.listFiles(prefix, 500, undefined, 1);
  const decodedFolders: DriveFolder[] = result.folders.map(folder => ({
    ...folder,
    name: decodeURIComponent(folder.name),
    path: decodeURIComponent(folder.path),
  }));
  const decodedFiles: DriveFile[] = result.files.map(file => ({
    ...file,
    name: decodeURIComponent(file.name),
    path: decodeURIComponent(file.path),
  }));
  setCache(prefix, decodedFolders, decodedFiles);
  return decodedFolders;
}

function buildBreadcrumbs(prefix: string, basePath: string): { name: string; path: string }[] {
  if (!prefix) return [];
  
  // Normalizar basePath
  const normalizedBasePath = basePath === "*" ? "" : basePath;
  
  const parts = prefix.replace(/\/$/, '').split('/').filter(Boolean);
  
  // Si el usuario tiene basePath, mostrar breadcrumbs relativos a su base
  if (normalizedBasePath) {
    const baseParts = normalizedBasePath.split('/').filter(Boolean);
    const relativeParts = parts.slice(baseParts.length);
    
    return relativeParts.map((name, i) => ({
      name,
      path: [...baseParts, ...relativeParts.slice(0, i + 1)].join('/'),
    }));
  }
  
  // Usuario sin basePath (acceso completo): breadcrumbs normales
  return parts.map((name, i) => ({
    name,
    path: parts.slice(0, i + 1).join('/'),
  }));
}

function sortItems<T extends { name: string; size?: number; lastModified?: string }>(
  items: T[],
  key: SortKey,
  dir: SortDir,
): T[] {
  return [...items].sort((a, b) => {
    let diff = 0;
    if (key === 'name')  diff = a.name.localeCompare(b.name);
    else if (key === 'size')  diff = (a.size ?? 0) - (b.size ?? 0);
    else if (key === 'date')  diff = new Date(a.lastModified ?? 0).getTime() - new Date(b.lastModified ?? 0).getTime();
    return dir === 'asc' ? diff : -diff;
  });
}

function sanitizePath(path: string): string {
  if (!path || typeof path !== 'string') return '';
  
  // Eliminar caracteres peligrosos y normalizar
  let sanitized = path
    .trim()
    .replace(/[<>:"|?*]/g, '') // Eliminar caracteres inválidos de Windows
    .replace(/\s+/g, ' ') // Normalizar espacios
    .replace(/\/+/g, '/') // Múltiples slashes a uno solo
    .replace(/^\/+/, '') // Eliminar slashes al inicio
    .replace(/\/+$/, ''); // Eliminar slashes al final
  
  // Si después de limpiar queda vacío o es muy corto, devolver vacío
  if (!sanitized || sanitized.length < 1) return '';
  
  // Verificar que no contenga patrones sospechosos
  const suspiciousPatterns = [
    /\.\.\//,  // Path traversal
    /\.\./,   // Double dot
    /^\./,    // Ocultos al inicio
    /^[^a-zA-Z0-9_\-]/, // Empieza con carácter inválido
  ];
  
  if (suspiciousPatterns.some(pattern => pattern.test(sanitized))) {
    console.warn('Path contains suspicious patterns:', path);
    return '';
  }
  
  return sanitized;
}

// Memoizar sanitizePath para evitar recreaciones
const memoizedSanitizePath = (() => {
  const cache = new Map<string, string>();
  return (path: string): string => {
    if (cache.has(path)) return cache.get(path)!;
    const result = sanitizePath(path);
    cache.set(path, result);
    return result;
  };
})();

export function useDrive() {
  // Restaurar ruta guardada al iniciar
  const [prefix, setPrefix] = useState(() => {
    try {
      return localStorage.getItem(CURRENT_PATH_KEY) || '';
    } catch {
      return '';
    }
  });

  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [zipPhase, setZipPhase] = useState('');
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [basePath, setBasePath] = useState<string>('');

  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  // Cargar basePath del usuario (se actualiza con cada carga)
  const loadBasePath = useCallback(async () => {
    try {
      const bp = await api.getUserBasePath();
      setBasePath(bp);
    } catch (err) {
      console.error('Error loading basePath:', err);
    }
  }, []);

  // Cargar basePath al inicio
  useEffect(() => {
    loadBasePath();
  }, [loadBasePath]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // ─── Load current folder ────────────────────────────────────────
  // opts.silent  → no mostrar spinner ni limpiar selección (uso tras mutaciones)
  // opts.force   → ignorar TTL del caché y revalidar siempre
  const load = useCallback(async (p: string, opts: { silent?: boolean; force?: boolean } = {}) => {
    // Recargar basePath por si cambió el usuario
    await loadBasePath();

    // Abortar petición anterior si existe
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // ── Stale-While-Revalidate: pintar caché si existe ────────────
    const cached = getCache(p);
    const isFresh = cached && Date.now() - cached.ts < CACHE_TTL;

    if (cached) {
      // Render instantáneo desde caché
      setFolders(cached.folders);
      setFiles(cached.files);
      setError('');
      setLoading(false);
    } else if (!opts.silent) {
      // Sin caché y no silent → mostrar spinner
      setLoading(true);
      setError('');
    } else {
      // Silent sin caché → no spinner, pero limpiar error
      setError('');
    }

    // Solo limpiar selección en navegaciones explícitas (no silent)
    if (!opts.silent) setSelected(new Set());

    // Si caché está fresca y no se fuerza, evitar la llamada
    if (isFresh && !opts.force) {
      if (ctrl === abortRef.current) abortRef.current = null;
      return;
    }

    // Safety timeout: si tarda >15s, forzar fin (solo si no había caché)
    const safetyTimeout = setTimeout(() => {
      if (ctrl === abortRef.current && mountedRef.current) {
        if (!cached) {
          setLoading(false);
          setError('La carga tardó demasiado. Pulsa reintentar.');
        }
        ctrl.abort();
      }
    }, 15000);

    try {
      // Prioridad ALTA (0) para navegación del usuario, BAJA (2) para revalidación silent
      const priority = opts.silent ? 2 : 0;
      const result = await api.listFiles(p, 500, ctrl.signal, priority);

      // Verificar si este controlador sigue siendo el activo
      const isActive = ctrl === abortRef.current && mountedRef.current;

      if (isActive) {
        // Decodificar nombres de archivo para mostrar espacios correctamente
        const decodedFolders = result.folders.map(folder => ({
          ...folder,
          name: decodeURIComponent(folder.name),
          path: decodeURIComponent(folder.path)
        }));

        const decodedFiles = result.files.map(file => ({
          ...file,
          name: decodeURIComponent(file.name),
          path: decodeURIComponent(file.path)
        }));

        setFolders(decodedFolders);
        setFiles(decodedFiles);
        setError('');
        setCache(p, decodedFolders, decodedFiles);
      }
    } catch (e) {
      // Verificar si este controlador sigue siendo el activo
      const isActive = ctrl === abortRef.current && mountedRef.current;
      
      if (isActive) {
        // Solo mostrar error si NO fue un abort intencional
        if (isActive && e instanceof Error && e.name !== 'AbortError') {
          setError(e.message);
        } else if (isActive) {
          setError('');
        } else {
          const errorMessage = e instanceof Error ? e.message : 'Error al cargar';
          setError(errorMessage);
          setFolders([]);
          setFiles([]);
        }
      }
    } finally {
      clearTimeout(safetyTimeout);
      
      // CRÍTICO: SIEMPRE desactivar loading si este es el controlador activo
      // Esto garantiza que nunca nos quedemos en loading=true
      if (ctrl === abortRef.current && mountedRef.current) {
        setLoading(false);
        abortRef.current = null;
      }
    }
  }, [loadBasePath]);

  useEffect(() => { 
    if (mountedRef.current) {
      load(prefix); 
    }
  }, [prefix, load]); // eslint-disable-line

  // ─── Navigation ─────────────────────────────────────────────────
  const navigate = useCallback((newPrefix: string) => {
    // Validar y sanitizar la ruta
    const sanitizedPrefix = memoizedSanitizePath(newPrefix);
    setPrefix(sanitizedPrefix);
    // Guardar ruta actual en localStorage
    try {
      localStorage.setItem(CURRENT_PATH_KEY, sanitizedPrefix);
    } catch (e) {
      console.warn('Failed to save current path:', e);
    }
  }, []);

  const goUp = useCallback(() => {
    const parts = prefix.replace(/\/$/, '').split('/').filter(Boolean);
    parts.pop();
    navigate(parts.length ? parts.join('/') : '');
  }, [prefix, navigate]);

  // ─── Sort ───────────────────────────────────────────────────────
  const toggleSort = useCallback((key: SortKey) => {
    setSortKey(prev => {
      if (prev === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else { setSortDir('asc'); }
      return key;
    });
  }, []);

  // ─── Selection ──────────────────────────────────────────────────
  const toggleSelect = useCallback((path: string, multi = false) => {
    // Normalizar selección: si corresponde a una carpeta del listado actual,
    // usar su path exacto (con "/" final). Esto evita que las carpetas se traten como archivos.
    const normalized = (() => {
      const noSlash = path.replace(/\/$/, '');
      const match = folders.find(f => f.path === path || f.path.replace(/\/$/, '') === noSlash);
      return match ? match.path : path;
    })();

    setSelected(prev => {
      // Comprobar si ESTABA seleccionado ANTES de modificar el set
      const wasSelected = prev.has(normalized)
        || prev.has(path)
        || prev.has(path.replace(/\/$/, ''))
        || prev.has(`${path.replace(/\/$/, '')}/`);

      if (!multi) {
        // Single-click: si ya estaba seleccionado (único), deseleccionar
        // Si estaba seleccionado junto con otros, reemplazar selección con solo este
        if (wasSelected) {
          if (prev.size === 1) {
            // Era el único seleccionado → deseleccionar
            return new Set();
          } else {
            // Había otros seleccionados → reemplazar con solo este
            return new Set([normalized]);
          }
        }
        // No estaba seleccionado → seleccionar solo este
        return new Set([normalized]);
      }

      // Multi (Ctrl/checkbox): toggle limpio
      const next = new Set(prev);
      // Limpiar todas las variantes
      next.delete(path);
      next.delete(path.replace(/\/$/, ''));
      next.delete(`${path.replace(/\/$/, '')}/`);

      if (wasSelected) {
        // Ya estaba → queda deseleccionado (ya lo borramos arriba)
      } else {
        next.add(normalized);
      }
      return next;
    });
  }, [folders]);

  const selectAll = useCallback(() => {
    setSelected(new Set([
      ...folders.map(f => f.path),
      ...files.map(f => f.path),
    ]));
  }, [folders, files]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  // ─── Filtered + sorted ──────────────────────────────────────────
  const q = search.toLowerCase();
  const filteredFolders = q ? folders.filter(f => f.name.toLowerCase().includes(q)) : folders;
  const filteredFiles   = q ? files.filter(f => f.name.toLowerCase().includes(q))   : files;
  const sortedFiles     = sortItems(filteredFiles, sortKey, sortDir);

  // ─── File operations ────────────────────────────────────────────
  const deleteSelected = useCallback(async () => {
    if (!selected.size) return;
    if (!confirm(`¿Eliminar ${selected.size} elemento(s)?`)) return;

    // Optimistic update: quitar items inmediatamente
    const sel = new Set(selected);
    setFolders(prev => prev.filter(f => !sel.has(f.path)));
    setFiles(prev => prev.filter(f => !sel.has(f.path)));
    setSelected(new Set());

    try {
      await api.deleteFiles(Array.from(sel));
      invalidateListCache(prefix);
      load(prefix, { silent: true, force: true });
    } catch (e) {
      console.error('❌ Delete failed, reloading:', e);
      // Reload completo para recuperar estado real
      invalidateListCache(prefix);
      load(prefix, { force: true });
    }
  }, [selected, prefix, load]);

  const downloadSelected = useCallback(async () => {
    const keys = Array.from(selected);
    if (!keys.length) return;
    
    const hasFolder = keys.some(k => k.endsWith('/'));
    const isSingleFile = keys.length === 1 && !hasFolder;
    
    // Archivo único: usar el mismo método que downloadFile (blob)
    if (isSingleFile) {
      try {
        const url = await api.getDownloadUrl(keys[0], true);
        
        // Descargar usando fetch para forzar descarga
        const response = await fetch(url);
        const blob = await response.blob();
        
        // Crear URL temporal del blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Forzar descarga
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = keys[0].split('/').pop() || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Liberar memoria
        window.URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error('❌ Error downloading file:', e);
      }
    } else {
      // Carpeta o múltiples: download-folder-zip → jobId → polling → downloadURL
      setDownloadingZip(true);
      setZipProgress(0);
      setZipPhase('iniciando');
      try {
        const { jobId } = await api.downloadAsZip(keys);
        setZipPhase('comprimiendo');

        const job = await api.pollJob(jobId, (pct, phase) => {
          setZipProgress(pct);
          if (phase) setZipPhase(phase);
        });

        if ((job.status === 'completed' || job.status === 'completed_with_errors') && job.downloadURL) {
          setZipPhase('descargando');
          setZipProgress(100);

          const a = document.createElement('a');
          a.href = job.downloadURL;
          a.download = 'download.zip';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          // Mantener modal 3 segundos para que el usuario vea que se descargó
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Mostrar errores si hubo problemas (después de descargar)
          if (job.status === 'completed_with_errors' && job.errorDetails && job.errorDetails.length > 0) {
            const errorList = job.errorDetails
              .map(e => `• ${e.source}\n  ${e.error}`)
              .join('\n\n');
            console.warn(`⚠️ ZIP con errores:\n${errorList}`);
          }
        } else {
          console.error('❌ ZIP falló, status:', job.status, job.errorDetails);
          if (job.errorDetails && job.errorDetails.length > 0) {
            const errorList = job.errorDetails
              .map(e => `• ${e.source}: ${e.error}`)
              .join('\n');
            console.error(`Detalles:\n${errorList}`);
          }
        }
      } catch (e) {
        console.error('❌ Error en descarga ZIP:', e);
      } finally {
        setDownloadingZip(false);
        setZipProgress(0);
        setZipPhase('');
      }
    }
  }, [selected]);

  const downloadFile = useCallback(async (key: string) => {
    try {
      // Obtener URL de descarga (forceDownload = true)
      const url = await api.getDownloadUrl(key, true);
      
      // Descargar usando fetch para forzar descarga
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Crear URL temporal del blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Forzar descarga
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = key.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Liberar memoria
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('❌ Download failed:', error);
    }
  }, []);

  const openFile = useCallback(async (key: string) => {
    try {
      const url = await api.getDownloadUrl(key, false);
      window.open(url, '_blank');
    } catch (error) {
      console.error('❌ Open failed:', error);
    }
  }, []);

  return {
    // State
    prefix,
    folders: filteredFolders,
    files: sortedFiles,
    allFolders: folders,
    allFiles: files,
    loading,
    downloadingZip, zipProgress, zipPhase,
    error,
    sortKey, sortDir, selected, search, setSearch,
    breadcrumbs: buildBreadcrumbs(prefix, basePath),
    basePath,
    // Actions
    navigate, goUp, load, toggleSort,
    toggleSelect, selectAll, clearSelection,
    deleteSelected, downloadSelected, downloadFile, openFile,
  };
}
